import { createAction } from '@reduxjs/toolkit';

// Interfaces
import {
    ISelectedFolder,
    IFolder,
    IUpdatedFoldersAction,
    folderActions,
    ISenderFolder,
} from '../../interfaces/folder.interfaces';
import { IMessage, ISelectedMessage, IUnreadCount } from '../../interfaces/message.interfaces';
import { Constants } from './constants';
import { FilterAction, SortAction } from '../../enums';
import { IMailBox, IAccess, IUserRole } from '../../interfaces/mailbox.interfaces';
import { setLocalStateMailboxId, setSessionStateFolderId } from './sideEffects';
import { INemSmsContactDetails } from '../../interfaces';

export const setFolderList = createAction(Constants.MAILBOX_FOLDER_LIST_SET, (folders: Array<IFolder>) => ({
    payload: { folders },
}));

export const setDocumentList = createAction(Constants.MAILBOX_DOCUMENTS_SET, (documentsList: Array<IMessage>) => ({
    payload: { documentsList },
}));

export const replaceDocument = createAction(Constants.MAILBOX_DOCUMENT_REPLACE, (document: IMessage) => ({
    payload: { document },
}));

export const setCurrentShownDocumentsCount = createAction(
    Constants.MAILBOX_DOCUMENTS_CURRENT_SHOWN_COUNT_SET,
    (currentShownCount: number) => ({
        payload: { currentShownCount },
    })
);

export const cleanDocumentList = createAction(Constants.MAILBOX_DOCUMENTS_CLEAN);

export const setSelectedDocuments = createAction(
    Constants.MAILBOX_DOCUMENTS_SELECT,
    (selectedDocuments: Array<ISelectedMessage>) => ({
        payload: { selectedDocuments },
    })
);

export const setFolderListUpdatedAction = createAction(
    Constants.MAILBOX_FOLDER_UPDATE_ACTION,
    (foldersUpdateAction: IUpdatedFoldersAction) => ({
        payload: { foldersUpdateAction },
    })
);

export const updateDocument = createAction(Constants.MAILBOX_DOCUMENT_UPDATE, (document: IMessage) => ({
    payload: { document },
}));

export const setUnreadCounter = createAction(Constants.MAILBOX_UNREAD_COUNT_SET, (unreadCounter: IUnreadCount) => ({
    payload: { unreadCounter },
}));

export const removeDocument = createAction(Constants.MAILBOX_DOCUMENT_REMOVE, (document: IMessage) => ({
    payload: { document },
}));

export const addDocument = createAction(Constants.MAILBOX_DOCUMENT_ADD, (document: IMessage) => ({
    payload: { document },
}));

export const updateFolderName = createAction(
    Constants.MAILBOX_FOLDER_UPDATE_NAME,
    (folderId: string, newName: string) => ({
        payload: { folderId, newName },
    })
);

export const removeDocumentById = createAction(Constants.MAILBOX_DOCUMENT_REMOVE_BY_ID, (documentId: string) => ({
    payload: { documentId },
}));

export const setSearchValue = createAction(Constants.MAILBOX_SET_SEARCH_VALUE, (searchValue: string) => ({
    payload: { searchValue },
}));

export const setSortOrder = createAction(Constants.MAILBOX_LIST_SORTORDER_ACTION, (sortOrder: SortAction) => ({
    payload: { sortOrder },
}));

export const setFilterBy = createAction(Constants.MAILBOX_LIST_FILTERBY_ACTION, (filterBy: FilterAction) => ({
    payload: { filterBy },
}));

export const setSearchInputText = createAction(Constants.MAILBOX_SET_SEARCH_INPUT_TEXT, (text: string) => ({
    payload: { text },
}));

export const setSearchOutsideMailbox = createAction(
    Constants.MAILBOX_SET_SEARCH_OUTSIDE_MAILBOX,
    (enabled: boolean) => ({
        payload: { enabled },
    })
);

export const unselectDocumentById = createAction(Constants.MAILBOX_DOCUMENT_UNSELECT_BY_ID, (documentId: string) => ({
    payload: { documentId },
}));

export const unselectDocumentByIds = createAction(Constants.MAILBOX_DOCUMENT_UNSELECT_BY_IDS, (documentIds: any) => ({
    payload: { documentIds },
}));

export const selectDocumentById = createAction(
    Constants.MAILBOX_DOCUMENT_SELECT_BY_ID,
    (documentId: string, version: number) => ({
        payload: { documentId, version },
    })
);

export const selectbysinglemessage = createAction(Constants.MAILBOX_DOCUMENT_SELECT_SINGLEMESSAGE, () => ({
    payload: { show: false },
}));

export const setEditNoteDocument = createAction(Constants.MAILBOX_SET_EDIT_NOTE_DOCUMENT, (document: IMessage) => ({
    payload: { document },
}));

export const setSelectedFolderAction = createAction(
    Constants.MAILBOX_SET_SELECTED_FOLDER_ACTION,
    (folderAction: folderActions) => ({
        payload: { folderAction },
    })
);

export const setIsMailboxMounted = createAction(Constants.MAILBOX_SET_IS_MAILBOX_MOUNTED, (mounted: boolean) => ({
    payload: { mounted },
}));

export const setIsMailboxSet = createAction(Constants.MAILBOX_SET_IS_MAILBOX_SET, (mailSet: boolean) => ({
    payload: { mailSet },
}));

export const replaceDocumentList = createAction(
    Constants.MAILBOX_DOCUMENTS_REPLACE,
    (documentListToReplace: Array<IMessage>) => ({
        payload: { documentListToReplace },
    })
);

export const showBatchError = createAction(Constants.MAILBOX_SHOW_BATCH_ERROR, (show: boolean) => ({
    payload: { show },
}));

export const deleteDocumentsFromList = createAction(
    Constants.MAILBOX_DOCUMENTS_DELETE_FROM_LIST,
    (documentsToDelete: Array<IMessage>) => ({
        payload: { documentsToDelete },
    })
);

export const waitForUpdating = createAction(Constants.MAILBOX_DOCUMENTS_WAIT_FOR_UPDATE, (wait: boolean) => ({
    payload: { wait },
}));

export const clickForMoveUpdating = createAction(Constants.MAILBOX_DOCUMENTS_MOVE_FOR_UPDATE, (click: boolean) => ({
    payload: { click },
}));

export const updateAccess = createAction(Constants.MAILBOX_ACCESS_UPDATE, (access: IAccess) => ({
    payload: { access },
}));

export const setUserRoles = createAction(Constants.MAILBOX_USER_ROLES, (userRoles: IUserRole[]) => ({
    payload: { userRoles },
}));

// Side Effect actions
export const selectMailbox = createAction(Constants.MAILBOX_SELECT, (mailbox: IMailBox) => {
    setLocalStateMailboxId(mailbox.id);

    return { payload: { mailbox } };
});

export const selectFolder = createAction(Constants.MAILBOX_FOLDER_SELECT, (folder: ISelectedFolder) => {
    setSessionStateFolderId(folder?.id);
    const folderData = folder?.name == 'Inbox' || 'Indbakke' ? folder : null;
    return {
        payload: { folder, folderData },
    };
});

export const setSelectSenderFolder = createAction(Constants.MAILBOX_SENDER_SELECT, (folder: ISenderFolder) => {
    return {
        payload: { folder },
    };
});

export const setSelectedSenderName = createAction(
    Constants.ADD_SELECTED_SENDER,
    (SelectedSender: any, isSenderClicked: boolean) => ({
        payload: { SelectedSender, isSenderClicked },
    })
);

export const setIsGreenlandSubscription = createAction(
    Constants.MAILBOX_IS_GREENLANDIC_SUBSCRIPTION,
    (isGreenlandSubscription: boolean) => {
        return {
            payload: { isGreenlandSubscription },
        };
    }
);

export const setIsReadUnreadAction = createAction(
    Constants.MAILBOX_IS_READ_UNREAD_ACTION,
    (isReadUnreadAction: boolean) => {
        return {
            payload: { isReadUnreadAction },
        };
    }
);

export const setContactDetails = createAction(
    Constants.MAILBOX_CONTACT_DETAILS,
    (contacts: INemSmsContactDetails[]) => {
        return {
            payload: { contacts },
        };
    }
);
